import { Spin, Table, Typography } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import {
    applicationConditionsListAction,
    applicationGetAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import {
    applicationEntityConditionsSelector,
    applicationRfiItemsSelector,
    applicationSelector,
} from '~Applications/selectors';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { ColumnType } from 'antd/lib/table';
import _ from 'lodash';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import { IDictionary } from '~utilities/IDictionary';
import ICondition from '~Api/Application/ICondition';
import IRfiItem from '~Api/Application/IRfiItem';
import RfiWorkflowStatusEnum from '~Api/Application/RfiWorkflowStatusEnum';
import UploadConditionDocument from './UploadConditionDocument';
import UploadedConditionDocumentList from './UploadedConditionDocumentList';

export default function RequiredInformation(): ReactElement {
    const { applicationUuid } = useParams<{ applicationUuid: string }>();
    const application: IApplication = useSelector((state: IGlobalState): IApplication => applicationSelector(state, applicationUuid));
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState): IDictionary<IDictionary<ICondition>> => applicationEntityConditionsSelector(state, applicationUuid));
    const rfiItems: IDictionary<IRfiItem> = useSelector((state: IGlobalState): IDictionary<IRfiItem> => applicationRfiItemsSelector(state, applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(applicationUuid));
        }
    }, [
        applicationEntityConditions,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!rfiItems) {
            dispatch(applicationRfiItemsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        dispatch,
        rfiItems,
    ]);

    if (!application || !applicationEntityConditions || !rfiItems) {
        return (
            <Layout applicationUuid={applicationUuid} section='required-information'>
                <Typography.Title level={2}>Required Information</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const columns: ColumnType<IRfiItem>[] = [
        {
            dataIndex: 'workflowStatus',
            render: (workflowStatus: RfiWorkflowStatusEnum): JSX.Element => {
                if (workflowStatus === RfiWorkflowStatusEnum.Supplied) {
                    return <CheckOutlined/>;
                }

                return <WarningOutlined/>;
            },
            title: '',
            width: '5%',
        },
        {
            render: (rfiItem: IRfiItem): string => {
                if (rfiItem.applicationBorrowerUuid) {
                    const applicationBorrower: IBorrower = _.find(application.borrowers, { uuid: rfiItem.applicationBorrowerUuid });

                    return applicationBorrower.dealBorrower.formattedName;
                }

                if (rfiItem.applicationPropertyUuid) {
                    const applicationProperty: IApplicationProperty = _.find(application.properties, { uuid: rfiItem.applicationPropertyUuid });

                    return applicationProperty.dealProperty.formattedAddress;
                }

                return 'Application';
            },
            title: 'Entity',
            width: '35%',
        },
        {
            render: (rfiItem: IRfiItem): string => {
                const entityUuid: string = rfiItem.applicationBorrowerUuid || rfiItem.applicationPropertyUuid || rfiItem.applicationUuid;

                return (applicationEntityConditions[entityUuid][`${entityUuid}:${rfiItem.applicationConditionUuid}`] || applicationEntityConditions[entityUuid][rfiItem.applicationConditionUuid]).name;
            },
            title: 'Condition',
        },
        {
            render: (rfiItem: IRfiItem): ReactElement => {
                let applicationBorrower: IBorrower = null;
                let applicationProperty: IApplicationProperty = null;

                if (rfiItem.applicationBorrowerUuid) {
                    applicationBorrower = _.find(application.borrowers, { uuid: rfiItem.applicationBorrowerUuid });
                }

                if (rfiItem.applicationPropertyUuid) {
                    applicationProperty = _.find(application.properties, { uuid: rfiItem.applicationPropertyUuid });
                }

                return (
                    <UploadedConditionDocumentList
                        application={application}
                        applicationBorrower={applicationBorrower}
                        applicationProperty={applicationProperty}
                        conditionType={rfiItem.conditionType}
                        conditionUuid={rfiItem.applicationConditionUuid}
                    />
                );
            },
            title: 'Documents',
            width: '30%',
        },
        {
            render: (rfiItem: IRfiItem): ReactElement => {
                let applicationBorrower: IBorrower = null;
                let applicationProperty: IApplicationProperty = null;

                if (rfiItem.applicationBorrowerUuid) {
                    applicationBorrower = _.find(application.borrowers, { uuid: rfiItem.applicationBorrowerUuid });
                }

                if (rfiItem.applicationPropertyUuid) {
                    applicationProperty = _.find(application.properties, { uuid: rfiItem.applicationPropertyUuid });
                }

                return (
                    <UploadConditionDocument
                        application={application}
                        applicationBorrower={applicationBorrower}
                        applicationProperty={applicationProperty}
                        conditionType={rfiItem.conditionType}
                        conditionUuid={rfiItem.applicationConditionUuid}
                    />
                );
            },
            title: 'Actions',
            width: '10%',
        },
    ];

    return (
        <Layout applicationUuid={applicationUuid} section='required-information'>
            <Typography.Title level={2}>Required Information</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(rfiItems)}
                pagination={false}
                rowKey='uuid'
            />
        </Layout>
    );
}
